import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { 
  CreditCard, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Search,
  FileText,
  RefreshCw,
  XCircle
} from 'lucide-react';

interface PaymentWorkflow {
  id: string;
  request_id: string;
  bid_id: string;
  insurer_id: string;
  status: string;
  amount: number;
  client_approval_date: string;
  insurer_confirmation_date: string;
  payment_due_date: string;
  completed_date: string;
  created_at: string;
  updated_at: string;
  insurance_requests: {
    car_make: string;
    car_model: string;
    email: string;
  };
  insurers: {
    name: string;
  };
  payment_invoices: {
    invoice_number: string;
    status: string;
    due_date: string;
  }[];
}

export function PaymentsManagement() {
  const [workflows, setWorkflows] = useState<PaymentWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedWorkflow, setSelectedWorkflow] = useState<PaymentWorkflow | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchWorkflows();
  }, []);

  async function fetchWorkflows() {
    try {
      setError(null);
      setIsRefreshing(true);

      const { data, error } = await supabase
        .from('payment_workflows')
        .select(`
          *,
          insurance_requests (
            car_make,
            car_model,
            email
          ),
          insurers (
            name
          ),
          payment_invoices (
            invoice_number,
            status,
            due_date
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWorkflows(data || []);
    } catch (err) {
      console.error('Error fetching payment workflows:', err);
      setError('Failed to load payment workflows');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }

  const filteredWorkflows = workflows.filter(workflow => {
    const matchesSearch = 
      workflow.insurance_requests.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      workflow.insurers.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (workflow.payment_invoices[0]?.invoice_number || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || workflow.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      awaiting_client_approval: { color: 'blue', icon: Clock, text: 'Awaiting Client Approval' },
      client_approved: { color: 'green', icon: CheckCircle, text: 'Client Approved' },
      client_rejected: { color: 'red', icon: XCircle, text: 'Client Rejected' },
      awaiting_insurer_confirmation: { color: 'yellow', icon: Clock, text: 'Awaiting Insurer' },
      terms_accepted: { color: 'green', icon: CheckCircle, text: 'Terms Accepted' },
      terms_rejected: { color: 'red', icon: XCircle, text: 'Terms Rejected' },
      invoice_generated: { color: 'blue', icon: FileText, text: 'Invoice Generated' },
      payment_pending: { color: 'yellow', icon: Clock, text: 'Payment Pending' },
      payment_received: { color: 'green', icon: CheckCircle, text: 'Payment Received' },
      payment_overdue: { color: 'red', icon: AlertCircle, text: 'Payment Overdue' },
      completed: { color: 'green', icon: CheckCircle, text: 'Completed' },
      cancelled: { color: 'gray', icon: XCircle, text: 'Cancelled' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || 
                  { color: 'gray', icon: AlertCircle, text: status };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="w-4 h-4 mr-1" />
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <CreditCard className="w-6 h-6 text-indigo-600 mr-2" />
          Payment Management
        </h2>
        <button
          onClick={fetchWorkflows}
          className="flex items-center px-3 py-2 bg-white border rounded-md text-gray-600 hover:bg-gray-50"
          disabled={isRefreshing}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative w-full sm:max-w-lg">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search by email, insurer, or invoice number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full sm:w-auto border rounded-md px-3 py-2"
        >
          <option value="all">All Statuses</option>
          <option value="awaiting_client_approval">Awaiting Client Approval</option>
          <option value="client_approved">Client Approved</option>
          <option value="awaiting_insurer_confirmation">Awaiting Insurer</option>
          <option value="payment_pending">Payment Pending</option>
          <option value="payment_overdue">Payment Overdue</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {/* Workflows Table */}
      <div className="bg-white shadow overflow-hidden rounded-lg">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client/Vehicle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Insurer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredWorkflows.map((workflow) => (
                <tr 
                  key={workflow.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedWorkflow(workflow)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {workflow.insurance_requests.email}
                    </div>
                    <div className="text-sm text-gray-500">
                      {workflow.insurance_requests.car_make} {workflow.insurance_requests.car_model}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{workflow.insurers.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      £{workflow.amount.toLocaleString('en-GB', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(workflow.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {workflow.payment_due_date ? 
                        new Date(workflow.payment_due_date).toLocaleDateString('en-GB') :
                        'Not set'
                      }
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {workflow.payment_invoices?.[0]?.invoice_number ? (
                      <div className="text-sm text-gray-900">
                        {workflow.payment_invoices[0].invoice_number}
                        <span className={`ml-2 text-xs ${
                          workflow.payment_invoices[0].status === 'paid' ? 'text-green-600' :
                          workflow.payment_invoices[0].status === 'overdue' ? 'text-red-600' :
                          'text-gray-500'
                        }`}>
                          ({workflow.payment_invoices[0].status})
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">Not generated</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Workflow Details Modal */}
      {selectedWorkflow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Payment Workflow Details</h3>
              <button
                onClick={() => setSelectedWorkflow(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Client</label>
                  <p className="mt-1">{selectedWorkflow.insurance_requests.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Insurer</label>
                  <p className="mt-1">{selectedWorkflow.insurers.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Vehicle</label>
                  <p className="mt-1">
                    {selectedWorkflow.insurance_requests.car_make} {selectedWorkflow.insurance_requests.car_model}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Amount</label>
                  <p className="mt-1">
                    £{selectedWorkflow.amount.toLocaleString('en-GB', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Workflow Timeline</h4>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className={`h-2 w-2 rounded-full ${
                        selectedWorkflow.client_approval_date ? 'bg-green-400' : 'bg-gray-200'
                      }`} />
                    </div>
                    <div className="ml-4 flex-1">
                      <p className="text-sm font-medium text-gray-900">Client Approval</p>
                      <p className="text-sm text-gray-500">
                        {selectedWorkflow.client_approval_date ? 
                          new Date(selectedWorkflow.client_approval_date).toLocaleString('en-GB') :
                          'Pending'
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className={`h-2 w-2 rounded-full ${
                        selectedWorkflow.insurer_confirmation_date ? 'bg-green-400' : 'bg-gray-200'
                      }`} />
                    </div>
                    <div className="ml-4 flex-1">
                      <p className="text-sm font-medium text-gray-900">Insurer Confirmation</p>
                      <p className="text-sm text-gray-500">
                        {selectedWorkflow.insurer_confirmation_date ? 
                          new Date(selectedWorkflow.insurer_confirmation_date).toLocaleString('en-GB') :
                          'Pending'
                        }
                      </p>
                    </div>
                  </div>
                  {selectedWorkflow.payment_invoices?.[0] && (
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className={`h-2 w-2 rounded-full ${
                          selectedWorkflow.payment_invoices[0].status === 'paid' ? 'bg-green-400' :
                          selectedWorkflow.payment_invoices[0].status === 'overdue' ? 'bg-red-400' :
                          'bg-gray-200'
                        }`} />
                      </div>
                      <div className="ml-4 flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          Invoice {selectedWorkflow.payment_invoices[0].invoice_number}
                        </p>
                        <p className="text-sm text-gray-500">
                          Due: {new Date(selectedWorkflow.payment_invoices[0].due_date).toLocaleDateString('en-GB')}
                          <span className={`ml-2 ${
                            selectedWorkflow.payment_invoices[0].status === 'paid' ? 'text-green-600' :
                            selectedWorkflow.payment_invoices[0].status === 'overdue' ? 'text-red-600' :
                            'text-gray-500'
                          }`}>
                            ({selectedWorkflow.payment_invoices[0].status})
                          </span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
